import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { getSuggestedArticles } from '@/services/api';
import type { SearchResult } from '@/types';

interface SuggestedArticlesProps {
  query: string;
  onArticleClick?: (articleId: string) => void;
  onSuggestionsLoad?: (articleIds: string[]) => void;
}

export function SuggestedArticles({ query, onArticleClick, onSuggestionsLoad }: SuggestedArticlesProps) {
  const [suggestions, setSuggestions] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setSuggestions([]);
      onSuggestionsLoad?.([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await getSuggestedArticles(searchQuery);
      const slicedResults = results.slice(0, 5);
      setSuggestions(slicedResults);
      onSuggestionsLoad?.(slicedResults.map(r => r.id));
    } catch {
      setSuggestions([]);
      onSuggestionsLoad?.([]);
    } finally {
      setIsLoading(false);
    }
  }, [onSuggestionsLoad]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSuggestions(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, fetchSuggestions]);

  if (!query.trim() || query.length < 3) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="p-3 border-b">
        <h4 className="text-sm font-medium flex items-center gap-2">
          Related articles
          {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
        </h4>
      </div>
      {suggestions.length > 0 ? (
        <ul className="divide-y">
          {suggestions.map((article) => {
            const href = article.category_slug && article.section_slug
              ? `/${article.category_slug}/${article.section_slug}/${article.slug}`
              : `/articles/${article.slug}`;

            return (
              <li key={article.id}>
                <Link
                  to={href}
                  onClick={() => onArticleClick?.(article.id)}
                  className="flex items-start gap-3 p-3 hover:bg-muted/50 transition-colors"
                >
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground line-clamp-1">
                      {article.title}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {article.category_name} / {article.section_name}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : !isLoading ? (
        <p className="p-3 text-sm text-muted-foreground">
          No related articles found
        </p>
      ) : null}
    </div>
  );
}
